import { useList, useDelete, useNavigation, useOne, useCreate, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowLeft, Save, Loader2 } from 'lucide-react';
import type { Offer } from '@natsepa/database';
import { ImageManager, type ManagedImage } from '../../components/ImageManager';
import { BilingualField } from '../../components/BilingualField';

// Helper function to generate slug from title
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

const CATEGORY_OPTIONS = [
  { value: 'accommodation', label: 'Accommodation' },
  { value: 'fnb', label: 'Food & Beverage' },
  { value: 'wedding-celebrations', label: 'Wedding & Celebrations' },
  { value: 'kids-programs', label: 'Kids Programs' },
];

export function OffersList() {
  const { data, isLoading, error } = useList<Offer>({ resource: 'offers' });
  const { mutate: deleteOne } = useDelete();
  const { edit, create } = useNavigation();

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this offer?')) {
      deleteOne({ resource: 'offers', id });
    }
  };

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (error) return (
    <div className="p-4"><div className="bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-800">Error loading offers: {error.message}</p></div></div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Offers & Packages</h1>
        <button onClick={() => create('offers')} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors">
          <Plus className="w-5 h-5" /> Add Offer
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valid Until</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.data.map((offer) => (
              <tr key={offer.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{offer.name}</div>
                  <div className="text-sm text-gray-500">{offer.slug}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {CATEGORY_OPTIONS.find(c => c.value === offer.category)?.label || offer.category}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{offer.price ? `IDR ${offer.price.toLocaleString()}` : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{offer.validUntil || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => edit('offers', offer.id)} className="p-2 text-gray-400 hover:text-gold-600 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(offer.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.data.length === 0 && <div className="p-8 text-center text-gray-500">No offers found. Click "Add Offer" to create one.</div>}
      </div>
    </div>
  );
}

// Create Offer
export function OffersCreate() {
  const navigate = useNavigate();
  const { mutate: createOffer, isPending } = useCreate();
  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    category: 'accommodation' as string,
    price: 0,
    validFrom: '',
    validUntil: '',
    linkUrl: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nameId) { alert('Please fill in the name'); return; }
    const slug = form.slug || generateSlug(form.nameId);
    createOffer(
      { resource: 'offers', values: { ...form, slug, name: form.nameId || form.nameEn, description: form.descriptionId || form.descriptionEn, price: form.price || undefined, validFrom: form.validFrom || undefined, validUntil: form.validUntil || undefined, linkUrl: form.linkUrl || undefined } },
      { onSuccess: (data) => { const newId = data?.data?.id; navigate(newId ? `/offers/edit/${newId}` : '/offers'); } }
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/offers')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Offer</h1>
          <p className="text-sm text-gray-500 mt-1">Add a new offer or package</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
          <BilingualField label="Name" valueEn={form.nameEn} valueId={form.nameId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })} required />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder={form.nameId ? generateSlug(form.nameId) : 'auto-generated'} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent">
              {CATEGORY_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
            </select>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Offer Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (IDR)</label>
              <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
              <input type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
              <input type="date" value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Link URL</label>
            <input type="text" value={form.linkUrl} onChange={(e) => setForm({ ...form, linkUrl: e.target.value })} placeholder="e.g., /rooms/presidential-suite" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800"><strong>Note:</strong> You can add images and descriptions after creating the offer.</p>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/offers')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Create & Continue
          </button>
        </div>
      </form>
    </div>
  );
}

// Edit Offer
export function OffersEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useOne<Offer>({ resource: 'offers', id: id! });
  const { mutate: updateOffer, isPending } = useUpdate();

  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    category: 'accommodation' as string,
    price: 0,
    validFrom: '',
    validUntil: '',
    linkUrl: '',
  });
  const [images, setImages] = useState<ManagedImage[]>([]);

  useEffect(() => {
    if (data?.data) {
      const offer = data.data;
      setForm({
        slug: offer.slug || '',
        nameEn: (offer as any).nameEn || (offer as any).name_en || offer.name || '',
        nameId: (offer as any).nameId || (offer as any).name_id || offer.name || '',
        descriptionEn: (offer as any).descriptionEn || (offer as any).description_en || offer.description || '',
        descriptionId: (offer as any).descriptionId || (offer as any).description_id || offer.description || '',
        shortDescriptionEn: (offer as any).shortDescriptionEn || (offer as any).short_description_en || offer.shortDescription || '',
        shortDescriptionId: (offer as any).shortDescriptionId || (offer as any).short_description_id || offer.shortDescription || '',
        category: offer.category || 'accommodation',
        price: offer.price || 0,
        validFrom: offer.validFrom || '',
        validUntil: offer.validUntil || '',
        linkUrl: offer.linkUrl || '',
      });
      if (offer.images) {
        setImages(offer.images.map((url, index) => ({ url, imageType: 'gallery' as const, sortOrder: index })));
      }
    }
  }, [data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateOffer(
      { resource: 'offers', id: id!, values: { ...form, price: form.price || undefined, validFrom: form.validFrom || undefined, validUntil: form.validUntil || undefined, linkUrl: form.linkUrl || undefined, images: images.map((img) => img.url) } },
      { onSuccess: () => navigate('/offers') }
    );
  };

  if (isLoading) return <div className="p-4">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/offers')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Offer</h1>
          <p className="text-sm text-gray-500 mt-1">{form.nameId || form.nameEn || 'Offer details'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
            <BilingualField label="Name" valueEn={form.nameEn} valueId={form.nameId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })} required />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input type="text" value={form.slug} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-gray-50" readOnly />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent">
                {CATEGORY_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
              </select>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Description</h2>
            <BilingualField label="Short Description" valueEn={form.shortDescriptionEn} valueId={form.shortDescriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'shortDescriptionEn' : 'shortDescriptionId']: value })} multiline rows={2} />
            <BilingualField label="Full Description" valueEn={form.descriptionEn} valueId={form.descriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'descriptionEn' : 'descriptionId']: value })} multiline rows={4} />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Offer Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (IDR)</label>
                <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
                <input type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                <input type="date" value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link URL</label>
              <input type="text" value={form.linkUrl} onChange={(e) => setForm({ ...form, linkUrl: e.target.value })} placeholder="e.g., /rooms/presidential-suite" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate('/offers')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
              {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Changes
            </button>
          </div>
        </form>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Offer Images</h2>
            <ImageManager entityType="offer" entityId={id!} images={images} imageType="gallery" multiple={true} maxImages={10} onChange={setImages} />
          </div>
        </div>
      </div>
    </div>
  );
}
